import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { Button } from './ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog'
import { RadioGroup, RadioGroupItem } from './ui/radio-group'
import { Label } from './ui/label'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { useDeleteAsset } from '../hooks/useQueries'
import { useApi } from '../api/hooks'
import { ComponentsDetachedNotice } from './equipment/ComponentsCard'

interface AssetDeleteDialogProps {
  asset: {
    uid: string
    name?: string | null
    asset_type_uid?: string | null
    children_count?: number | null
  }
  org: string
  onDeleted: () => void
}

export function AssetDeleteDialog({ asset, org, onDeleted }: AssetDeleteDialogProps) {
  const { t } = useTranslation('assets')
  const { request } = useApi()
  const deleteMutation = useDeleteAsset(org)

  const [open, setOpen] = useState(false)
  const [strategy, setStrategy] = useState<'cascade' | 'migrate'>('cascade')
  const [migrateTargetUid, setMigrateTargetUid] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const hasChildren = (asset.children_count ?? 0) > 0

  const handleSearchAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    try {
      const queryParams: Record<string, string> = { limit: '20' }
      if (query) {
        queryParams.q = query
        queryParams.sort = 'name'
      } else {
        queryParams.sort = '-updated_at'
      }
      if (asset.asset_type_uid) {
        queryParams.asset_type_uid = asset.asset_type_uid
      }

      const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
        params: {
          path: { orgCode: org },
          query: queryParams,
        },
      })

      const assets = (response as any)?.data || []
      return assets
        .filter((a: any) => a.uid !== asset.uid)
        .map((a: any) => ({
          value: a.uid,
          label: a.name || a.uid,
        }))
    } catch {
      return []
    }
  }, [request, org, asset.asset_type_uid, asset.uid])

  const handleConfirm = async () => {
    setIsDeleting(true)
    try {
      await deleteMutation.mutateAsync({
        assetUid: asset.uid,
        childrenStrategy: hasChildren ? strategy : 'cascade',
        migrateTo: hasChildren && strategy === 'migrate' ? (migrateTargetUid ?? undefined) : undefined,
      })
      setOpen(false)
      onDeleted()
    } catch {
      // Error is handled by the mutation state
    } finally {
      setIsDeleting(false)
    }
  }

  const canConfirm = !isDeleting && (!hasChildren || strategy === 'cascade' || (strategy === 'migrate' && migrateTargetUid))

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setStrategy('cascade'); setMigrateTargetUid(null) } }}>
      <AlertDialogTrigger asChild>
        <Button
          variant="destructive"
          size="sm"
          data-testid="delete-asset-btn"
          aria-label={t('delete.button')}
          title={t('delete.button')}
        >
          <AppIcon path={mdiTrashCanOutline} size="sm" className="md:mr-1" />
          <span className="hidden md:inline">{t('delete.button')}</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('delete.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {hasChildren
              ? t('delete.confirm_with_children', { name: asset.name, count: asset.children_count })
              : t('delete.confirm_simple', { name: asset.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ComponentsDetachedNotice org={org} assetUid={asset.uid} enabled={open} />

        {hasChildren && (
          <div className="space-y-4 py-2">
            <RadioGroup value={strategy} onValueChange={(v) => setStrategy(v as 'cascade' | 'migrate')}>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="cascade" id="cascade" data-testid="strategy-cascade" />
                <div>
                  <Label htmlFor="cascade" className="font-medium">{t('delete.strategy_cascade')}</Label>
                  <p className="text-sm text-muted-foreground">{t('delete.strategy_cascade_description')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="migrate" id="migrate" data-testid="strategy-migrate" />
                <div>
                  <Label htmlFor="migrate" className="font-medium">{t('delete.strategy_migrate')}</Label>
                  <p className="text-sm text-muted-foreground">{t('delete.strategy_migrate_description')}</p>
                </div>
              </div>
            </RadioGroup>

            {strategy === 'migrate' && (
              <div className="pl-6">
                <Label className="text-sm mb-1 block">{t('delete.select_target')}</Label>
                <ApiSearchableSelect
                  placeholder={t('delete.select_target_placeholder')}
                  onSearch={handleSearchAssets}
                  onValueChange={setMigrateTargetUid}
                  value={migrateTargetUid ?? undefined}
                  data-testid="migrate-target-select"
                />
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-delete-asset-btn">
            {t('common:actions.cancel', 'Cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleConfirm() }}
            disabled={!canConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="confirm-delete-asset-btn"
          >
            {isDeleting ? t('common:actions.deleting', 'Deleting...') : t('delete.button')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
